import { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const CustomCursor = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    // Detect touch devices and skip cursor entirely
    const isTouch = window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
    setIsTouchDevice(isTouch);
  }, []);

  
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  
  // Fast spring for white dot (real-time)
  const fastSpringConfig = { damping: 50, stiffness: 800 };
  const cursorXFast = useSpring(cursorX, fastSpringConfig);
  const cursorYFast = useSpring(cursorY, fastSpringConfig);
  
  // Slower spring for gold aura (slight lag)
  const slowSpringConfig = { damping: 30, stiffness: 300 };
  const cursorXSlow = useSpring(cursorX, slowSpringConfig);
  const cursorYSlow = useSpring(cursorY, slowSpringConfig);

  useEffect(() => {
    const moveCursor = (e: MouseEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      setIsVisible(true);
    };

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    const addHoverListeners = () => {
      const hoverElements = document.querySelectorAll('a, button, [data-cursor-hover]');
      hoverElements.forEach((el) => {
        el.addEventListener('mouseenter', () => setIsHovering(true));
        el.addEventListener('mouseleave', () => setIsHovering(false));
      });
    };

    window.addEventListener('mousemove', moveCursor);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);
    
    addHoverListeners();
    
    // Re-add listeners when DOM changes
    const observer = new MutationObserver(addHoverListeners);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      observer.disconnect();
    };
  }, [cursorX, cursorY]);

  if (isTouchDevice) return null;

  return (
    <>
      {/* Gold glow aura - behind cursor (slightly lagging) */}
      <motion.div
        className="fixed pointer-events-none z-[9998]"
        style={{
          left: cursorXSlow,
          top: cursorYSlow,
          translateX: '-50%',
          translateY: '-50%',
        }}
      >
        <motion.div
          animate={{
            scale: isHovering ? 2 : 1,
            opacity: isVisible ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Gold glow */}
          <div 
            className="w-24 h-24 rounded-full"
            style={{
              background: 'radial-gradient(circle, hsl(42 50% 59% / 0.12) 0%, hsl(42 50% 59% / 0.05) 40%, transparent 70%)',
              filter: 'blur(2px)',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Main cursor dot - white (real-time) */}
      <motion.div
        className="fixed pointer-events-none z-[9999] mix-blend-difference"
        style={{
          left: cursorXFast,
          top: cursorYFast,
          translateX: '-50%',
          translateY: '-50%',
        }}
      >
        <motion.div
          animate={{
            scale: isHovering ? 1.5 : 1,
            opacity: isVisible ? 1 : 0,
          }}
          transition={{ duration: 0.2 }}
        >
          {/* White cursor dot */}
          <div className="w-4 h-4 rounded-full bg-white" />
        </motion.div>
      </motion.div>
    </>
  );
};

export default CustomCursor;
